import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, Send, RefreshCw, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import ProPortalHeader from '@/components/ProPortalHeader';
import { formatApiError } from '@/utils/apiError';

const BRAND = { probound: { label: 'ProBound', color: '#2F5E96' }, proepoxy: { label: 'ProEpoxy', color: '#7A0B7A' }, prodecor: { label: 'ProDecor', color: '#B8860B' } };
const TABS = [
  { id: 'attention', label: 'Needs attention' },
  { id: 'expired', label: 'Expired' },
  { id: 'expiring', label: 'Expiring ≤30 days' },
  { id: 'all', label: 'All with insurance' },
];
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

function DaysPill({ row }) {
  if (row.status === 'expired') return <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-800" data-testid={`ins-days-${row.user_id}-${row.brand}`}>Expired {Math.abs(row.days_left)}d ago</span>;
  if (row.status === 'expiring') return <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid={`ins-days-${row.user_id}-${row.brand}`}>{row.days_left}d left</span>;
  return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800" data-testid={`ins-days-${row.user_id}-${row.brand}`}>{row.days_left}d left</span>;
}

export default function StaffInsuranceExpiry() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('attention');
  const [busy, setBusy] = useState('');

  const load = async () => {
    try {
      const r = await fetch(`${API_URL}/api/staff/insurance-expiry`, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(formatApiError(d));
      setData(d);
    } catch (e) { toast.error(e.message || 'Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  const remind = async (row) => {
    setBusy(`${row.user_id}:${row.brand}`);
    try {
      const r = await fetch(`${API_URL}/api/staff/insurance-expiry/${row.user_id}/${row.brand}/remind`, { method: 'POST', credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(formatApiError(d));
      toast.success(`Reminder sent to ${row.company_name || row.name} · ${d.sms ? 'text ✓' : 'no text'} · ${d.email ? 'email ✓' : 'email failed'}`);
      await load();
    } catch (e) { toast.error(e.message || 'Send failed'); } finally { setBusy(''); }
  };

  const runNow = async () => {
    setBusy('run');
    try {
      const r = await fetch(`${API_URL}/api/staff/insurance-expiry/run`, { method: 'POST', credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(formatApiError(d));
      toast.success(`Checked: ${d.reminded} reminder${d.reminded === 1 ? '' : 's'} sent, ${d.expired_alerts} expired alert${d.expired_alerts === 1 ? '' : 's'}`);
      await load();
    } catch (e) { toast.error(e.message || 'Run failed'); } finally { setBusy(''); }
  };

  const rows = useMemo(() => (data?.rows || []).filter((r) => {
    if (tab === 'all') return true;
    if (tab === 'expired') return r.status === 'expired';
    if (tab === 'expiring') return r.status === 'expiring';
    return r.status !== 'ok';
  }), [data, tab]);
  const c = data?.counts;

  return (
    <div className="min-h-screen bg-[#E8ECEF]">
      <ProPortalHeader activeSection="staff" />
      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4">
        <button onClick={() => navigate('/staff')} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-2" data-testid="ins-back"><ArrowLeft className="w-4 h-4" /> Staff Dashboard</button>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#27292E] flex items-center gap-2"><ShieldAlert className="w-7 h-7 text-amber-600" /> Insurance Expiry</h1>
            <p className="text-sm text-slate-600">Ox Approved installers are texted and emailed automatically {data?.remind_days ?? 30} days before their insurance expires (once per policy, 09:00 daily). Expired policies raise a staff alert.</p>
          </div>
          <div className="flex items-center gap-2">
            {c && (
              <div className="text-xs text-slate-600 flex items-center gap-3" data-testid="ins-counts">
                <span className="font-bold text-rose-700">{c.expired} expired</span>
                <span className="font-bold text-amber-700">{c.expiring} expiring</span>
                <span className="text-emerald-700">{c.ok} in date</span>
              </div>
            )}
            <Button size="sm" variant="outline" onClick={runNow} disabled={!!busy} data-testid="ins-run-now"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy === 'run' ? 'animate-spin' : ''}`} /> Run check now</Button>
          </div>
        </div>

        <div className="inline-flex flex-wrap bg-white border rounded-lg p-0.5 mb-3" data-testid="ins-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`ins-tab-${t.id}`} className={`px-3 py-1.5 text-sm font-semibold rounded-md ${tab === t.id ? 'bg-[#27292E] text-white' : 'text-gray-600 hover:bg-gray-50'}`}>{t.label}</button>
          ))}
        </div>

        <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
          {!data && <p className="p-6 text-sm text-gray-500">Loading…</p>}
          {data && rows.length === 0 && <p className="p-6 text-sm text-gray-500" data-testid="ins-empty">{tab === 'attention' ? 'Nothing needs attention — every approved installer\'s insurance is in date.' : 'No installers in this view.'}</p>}
          {rows.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <tr><th className="text-left px-3 py-2">Installer</th><th className="text-left px-3 py-2">Brand</th><th className="text-left px-3 py-2">Insurer / policy</th><th className="text-left px-3 py-2">Expires</th><th className="text-left px-3 py-2">Reminder</th><th className="px-3 py-2" /></tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={`${r.user_id}-${r.brand}`} className={r.status === 'expired' ? 'bg-rose-50/40' : r.status === 'expiring' ? 'bg-amber-50/40' : ''} data-testid={`ins-row-${r.user_id}-${r.brand}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-gray-900">{r.company_name || r.name}</div>
                      <div className="text-xs text-gray-500">{r.name} · {r.email || 'no email'}{r.phone ? ` · ${r.phone}` : ''}{!r.approved && <span className="ml-1 text-gray-400">· not yet approved</span>}</div>
                    </td>
                    <td className="px-3 py-2"><span className="text-[10px] font-bold text-white rounded px-1.5 py-0.5" style={{ background: BRAND[r.brand]?.color }}>{BRAND[r.brand]?.label || r.brand}</span></td>
                    <td className="px-3 py-2 text-xs"><div>{r.insurance_company || '—'}</div><div className="text-gray-500">{r.policy_number || '—'}{r.has_certificate && <a className="ml-1 text-blue-700 underline" href={`${API_URL}/api/installer/approvals/${r.brand}/stage4/certificate?user_id=${r.user_id}`} target="_blank" rel="noreferrer" data-testid={`ins-cert-${r.user_id}-${r.brand}`}><FileText className="w-3 h-3 inline" /> cert</a>}</div></td>
                    <td className="px-3 py-2"><div className="text-xs text-gray-800">{r.expiry_label}</div><DaysPill row={r} /></td>
                    <td className="px-3 py-2 text-xs text-gray-600">{r.reminder_sent_at ? <>Sent {fmt(r.reminder_sent_at)}<div className="text-gray-400">{r.reminder_channels?.sms ? 'text' : ''}{r.reminder_channels?.sms && r.reminder_channels?.email ? ' + ' : ''}{r.reminder_channels?.email ? 'email' : ''}</div></> : <span className="text-gray-400">Not sent</span>}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => remind(r)} disabled={!!busy || (!r.email && !r.phone)} data-testid={`ins-remind-${r.user_id}-${r.brand}`}>
                        {busy === `${r.user_id}:${r.brand}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} {r.reminder_sent_at ? 'Send again' : 'Send reminder'}
                      </Button>
                      <Button size="sm" variant="ghost" className="ml-1" onClick={() => navigate(`/staff/approvals?open=${r.user_id}:${r.brand}`)} data-testid={`ins-open-${r.user_id}-${r.brand}`}>Approval →</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
